import { apiClient } from "@/services/apiClient";

// ---- Types ----

export interface SiteStorageSummaryDto {
  siteCollectionId: number;
  guid: string;
  title: string;
  url: string;
  groupId: string | null;
  owner: string | null;
  template: string | null;
  storageQuotaMb: number;
  storageUsedMb: number;
  storageUsagePercent: number;
  lastSnapshotDate: string | null;
}

export interface SiteStorageSnapshotDto {
  snapshotId: number;
  storageQuotaMb: number;
  storageUsedMb: number;
  storageUsagePercent: number;
  snapshotDate: string;
}

export interface SiteStorageDetailDto {
  site: SiteStorageSummaryDto;
  history: SiteStorageSnapshotDto[];
}

export interface PaginatedSiteStorageDto {
  items: SiteStorageSummaryDto[];
  totalCount: number;
  skip: number;
  take: number;
  pageCount: number;
  currentPage: number;
}

export interface TriggerArchiveReviewRequest {
  siteCollectionId: number;
  siteUrl: string;
  siteTitle: string;
  assignToEmail: string;
  reviewName: string;
  cutoffDate: string;
  targetExtensions: string[];
  sessionGuid?: string;
}

export interface TriggerArchiveReviewResponse {
  reviewGuid: string;
  reportRunGuid: string | null;
}

export interface SiteStorageStateDto {
  databaseStorageQuotaMb: number;
  liveStorageQuotaMb: number;
  storageUsedMb: number;
  uniqueId: string;
  needsConfirmation: boolean;
}

export interface ArchiveRunSummaryDto {
  m365ReportRunId: number;
  m365ReportId: number;
  stageName: string;
  reportTitle: string;
  reportStatus: string;
  reportResult?: string;
  guid: string;
  parentArchiveGuid?: string;
  siteGuid?: string;
  groupGuid?: string;
  siteUrl?: string;
  createUser: string;
  createDt: string;
  reportStartDate?: string;
  reportEndDate?: string;
}

export interface QueueArchiveStageRequest {
  parentArchiveGuid?: string;
}

// ---- API functions ----

export async function getAllSiteStorage(): Promise<SiteStorageSummaryDto[]> {
  const res = await apiClient.get<SiteStorageSummaryDto[]>("/api/admin/sharepoint/sites");
  return res.data;
}

export async function getSiteStoragePaginated(
  skip: number = 0, take: number = 100, sortBy: string = "usage",
  options: { sortDirection?: "asc" | "desc"; search?: string; siteType?: "All Sites" | "SharePoint" | "OneDrive"; signal?: AbortSignal } = {}
): Promise<PaginatedSiteStorageDto> {
  const { signal, ...filters } = options;
  const res = await apiClient.get<PaginatedSiteStorageDto>("/api/admin/sharepoint/sites/paginated", {
    params: { skip, take, sortBy, ...filters },
    signal,
  });
  return res.data;
}

export async function getSiteStorageDetail(siteCollectionId: number): Promise<SiteStorageDetailDto> {
  const res = await apiClient.get<SiteStorageDetailDto>(
    `/api/admin/sharepoint/sites/${siteCollectionId}/detail`
  );
  return res.data;
}

export async function getSiteStorageDetailByGuid(siteGuid: string): Promise<SiteStorageDetailDto> {
  const res = await apiClient.get<SiteStorageDetailDto>(
    `/api/admin/sharepoint/sites/guid/${encodeURIComponent(siteGuid)}/detail`
  );
  return res.data;
}

export async function getSiteStorageByGroup(groupId: string): Promise<SiteStorageSummaryDto | null> {
  try {
    const res = await apiClient.get<SiteStorageSummaryDto>(
      `/api/sharepoint/${encodeURIComponent(groupId)}/storage`
    );
    return res.data;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

export async function triggerArchiveReview(
  data: TriggerArchiveReviewRequest
): Promise<TriggerArchiveReviewResponse> {
  const res = await apiClient.post<TriggerArchiveReviewResponse>(
    "/api/admin/sharepoint/archive-review/trigger",
    data
  );
  return res.data;
}

export async function triggerArchiveReviewForGroup(
  groupId: string,
  data: TriggerArchiveReviewRequest
): Promise<TriggerArchiveReviewResponse> {
  const res = await apiClient.post<TriggerArchiveReviewResponse>(
    `/api/sharepoint/${encodeURIComponent(groupId)}/archive-review`,
    data
  );
  return res.data;
}

export async function increaseStorage(
  siteGuid: string,
  newQuotaMb: number
): Promise<void> {
  await apiClient.post(
    `/api/admin/sharepoint/sites/${encodeURIComponent(siteGuid)}/set-storage-immediate`,
    { newQuotaMb, currentQuotaMb: newQuotaMb }
  );
}

export async function getStorageState(
  siteGuid: string
): Promise<SiteStorageStateDto> {
  const response = await apiClient.get<SiteStorageStateDto>(
    `/api/admin/sharepoint/sites/${encodeURIComponent(siteGuid)}/storage-state`
  );
  return response.data;
}

export async function setStorageImmediate(
  siteGuid: string,
  newQuotaMb: number,
  currentQuotaMb: number
): Promise<any> {
  const response = await apiClient.post(
    `/api/admin/sharepoint/sites/${encodeURIComponent(siteGuid)}/set-storage-immediate`,
    { newQuotaMb, currentQuotaMb }
  );
  return response.data;
}

export async function queueStorageUpdate(
  siteGuid: string,
  newQuotaMb: number,
  currentQuotaMb: number
): Promise<{ status: string; reportRunGuid: string; message: string }> {
  const response = await apiClient.post(
    `/api/admin/sharepoint/sites/${encodeURIComponent(siteGuid)}/set-storage-immediate`,
    { newQuotaMb, currentQuotaMb }
  );
  return response.data;
}

export async function getAdminArchiveRuns(take: number = 200): Promise<ArchiveRunSummaryDto[]> {
  const response = await apiClient.get<ArchiveRunSummaryDto[]>("/api/admin/sharepoint/archive-runs", {
    params: { take }
  });
  return response.data;
}

export async function getAdminArchiveRunsBySite(siteGuid: string): Promise<ArchiveRunSummaryDto[]> {
  const response = await apiClient.get<ArchiveRunSummaryDto[]>(
    `/api/admin/sharepoint/sites/guid/${encodeURIComponent(siteGuid)}/archive-runs`
  );
  return response.data;
}

export async function startAdminArchive(siteGuid: string, parentArchiveGuid?: string): Promise<{ archiveRequestGuid: string }> {
  const response = await apiClient.post(
    `/api/admin/sharepoint/sites/${encodeURIComponent(siteGuid)}/archive/start`,
    { parentArchiveGuid } as QueueArchiveStageRequest
  );
  return response.data;
}

export async function queueAdminArchiveReadonly(siteGuid: string, parentArchiveGuid?: string): Promise<{ archiveRequestGuid: string }> {
  const response = await apiClient.post(
    `/api/admin/sharepoint/sites/${encodeURIComponent(siteGuid)}/archive/readonly`,
    { parentArchiveGuid } as QueueArchiveStageRequest
  );
  return response.data;
}

export async function queueAdminArchiveCleanup(siteGuid: string, parentArchiveGuid?: string): Promise<{ archiveRequestGuid: string }> {
  const response = await apiClient.post(
    `/api/admin/sharepoint/sites/${encodeURIComponent(siteGuid)}/archive/cleanup`,
    { parentArchiveGuid } as QueueArchiveStageRequest
  );
  return response.data;
}

export async function getGroupArchiveRuns(groupId: string): Promise<ArchiveRunSummaryDto[]> {
  const response = await apiClient.get<ArchiveRunSummaryDto[]>(
    `/api/sharepoint/${encodeURIComponent(groupId)}/archive-runs`
  );
  return response.data;
}

export async function startGroupArchive(groupId: string, parentArchiveGuid?: string): Promise<{ archiveRequestGuid: string }> {
  const response = await apiClient.post(
    `/api/sharepoint/${encodeURIComponent(groupId)}/archive/start`,
    { parentArchiveGuid } as QueueArchiveStageRequest
  );
  return response.data;
}
